import React, { useState } from 'react';
import { FormControl, InputGroup } from 'react-bootstrap';
import { Deal, DealNote } from '../models/shared/Deal';
import { useText } from '../context/TextContext';
import { useUser } from '../context/UserContext';
import DealModalSection from './DealModalSection';
import { formatDate } from '../utils/date';
import { useOrganization } from '../context/OrganizationContext';
import Gravatar from './Gravatar';
import { AdvancedButton } from './AdvancedButton';
import CommentAttachments from './CommentAttachments';
import { Attachement } from '../models/shared/Attachment';
import { useUploadDealAttachment } from '../hooks/useUploadDealAttachement';
import { toast } from 'react-toastify';
import AttachmentPreview from './AttachmentPreview';

interface Props {
    deal: Deal;
    onDealUpdate: (update: Partial<Deal>) => Promise<void>;
    className?: string;
    readOnly?: boolean;
}

export default function CommentsSection(props: Props) {
    const text = useText();
    const { user } = useUser();
    const [newComment, setNewComment] = useState('');
    const { members, orgId } = useOrganization();
    const [processing, setProcessing] = useState(false);
    const [newCommentFiles, setNewCommentFiles] = useState<File[]>([]);
    const uploadDealAttachment = useUploadDealAttachment(orgId);
    const [previewIndex, setPreviewIndex] = useState<number>(-1);
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);

    const notes = props.deal.notes || [];
    const dealAttachments = props.deal.attachments || [];

    const handleAddComment = async () => {
        if ((!newComment.trim() && newCommentFiles.length === 0) || !user?.email) {
            return;
        }
        setProcessing(true);

        try {
            // Upload any new files during comment creation
            const uploadedAttachments: Attachement[] = [];
            for (const file of newCommentFiles) {
                const uploadedAttachment = await uploadDealAttachment(props.deal.id!, file);
                if (uploadedAttachment) {
                    uploadedAttachments.push(uploadedAttachment);
                }
            }
            const attachmentPaths = uploadedAttachments.map(att => att.path);

            const newNote: DealNote = {
                createdAt: new Date(),
                createdBy: user.email,
                content: newComment.trim(),
                attachmentPaths,
            };

            // Update deal with new comment and attachments
            const updatedAttachments = [...dealAttachments, ...uploadedAttachments];

            await props.onDealUpdate({
                notes: [...notes, newNote],
                attachments: updatedAttachments,
            });

            setNewComment('');
            setNewCommentFiles([]);
        } catch (error) {
            console.error('Error adding comment:', error);
            toast.error(text.defaultError);
        }

        setProcessing(false);
    };

    const handleFileSelect = (files: File[]) => {
        setNewCommentFiles(prev => [...prev, ...files]);
    };


    const handleImageClick = (attachment: Attachement) => {
        const attachmentIndex = dealAttachments.findIndex(att => att.path === attachment.path);
        if (attachmentIndex !== -1) {
            setPreviewIndex(attachmentIndex);
            setIsPreviewOpen(true);
        }
    };

    const handleClosePreview = () => {
        setIsPreviewOpen(false);
        setPreviewIndex(-1);
    };

    const handleDeleteComment = async (index: number) => {
        setProcessing(true);
        const updatedNotes = notes.filter((_, i) => i !== index);
        await props.onDealUpdate({
            notes: updatedNotes
        });
        setProcessing(false);
    };

    const getCommentsDisplay = () => {
        if (notes.length === 0) {
            return <span className="text-muted">{text.noComments}</span>;
        }
        return `${notes.length} ${notes.length === 1 ? text.comment : text.comments}`;
    };

    return (
        <>
            <DealModalSection
                title={text.comments}
                sizeDependencies={[props.deal.notes]}
                alwaysVisibleChildren={
                    <div className="mb-2 small text-muted">
                        {getCommentsDisplay()}
                    </div>
                }
            >
                <div className='d-flex flex-column gap-3'>
                    <div className='d-flex flex-column gap-2'>
                        {notes.map((note, index) => {
                            const member = members.find(m => m.email === note.createdBy);
                            const noteAttachmentPaths = note.attachmentPaths || [];
                            const noteAttachments = dealAttachments.filter(att => noteAttachmentPaths.includes(att.path));
                            return (
                                <div key={index} className='border rounded p-2'>
                                    <div className='d-flex justify-content-between align-items-start mb-1'>
                                        <div className='center'>
                                            {member && <Gravatar key={member.email} identifier={member.email} size='xs' default='wavatar' className='me-1' />}
                                            <small className='text-muted'>
                                                <strong>{note.createdBy}</strong>
                                                {' • '}
                                                {formatDate(note.createdAt)}
                                            </small>
                                        </div>
                                        {!props.readOnly && user?.email === note.createdBy && (
                                            <AdvancedButton
                                                processing={processing}
                                                variant='link'
                                                size='sm'
                                                className='p-0 text-danger'
                                                onClick={() => handleDeleteComment(index)}
                                            >
                                                <i className='bi bi-trash' />
                                            </AdvancedButton>
                                        )}
                                    </div>
                                    {note.content && (
                                        <div style={{ whiteSpace: 'pre-wrap' }} className="mb-2">{note.content}</div>
                                    )}
                                    {noteAttachments.length > 0 && (
                                        <div className="d-flex flex-wrap gap-2">
                                            {noteAttachments.map((attachment) => (
                                                <div
                                                    key={attachment.path}
                                                    className="border rounded overflow-hidden"
                                                    style={{
                                                        width: '80px',
                                                        height: '80px',
                                                        cursor: 'pointer'
                                                    }}
                                                    onClick={() => handleImageClick(attachment)}
                                                >
                                                    <img
                                                        src={attachment.url}
                                                        alt={attachment.name}
                                                        style={{
                                                            width: '100%',
                                                            height: '100%',
                                                            objectFit: 'cover'
                                                        }}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )
                        })}
                    </div>

                    {/* Add new comment */}
                    {!props.readOnly && (
                        <div className="d-flex flex-column gap-2">
                            <div className="position-relative">
                                <InputGroup>
                                    <FormControl
                                        as='textarea'
                                        rows={3}
                                        placeholder={text.writeComment}
                                        value={newComment}
                                        onChange={(e) => setNewComment(e.target.value)}
                                        disabled={processing}
                                        style={{
                                            paddingBottom: '35px',
                                            resize: 'none'
                                        }}
                                    />
                                    <AdvancedButton
                                        processing={processing}
                                        variant='outline-primary'
                                        icon='bi-send'
                                        size='sm'
                                        onClick={handleAddComment}
                                        disabled={!newComment.trim() && newCommentFiles.length === 0}
                                    />
                                </InputGroup>

                                {/* Attachment controls at bottom of textarea */}
                                <div className="position-absolute d-flex align-items-center gap-2" style={{ bottom: '10px', left: '12px', zIndex: 10 }}>
                                    <CommentAttachments
                                        files={newCommentFiles}
                                        onFilesChange={setNewCommentFiles}
                                        onFileSelect={handleFileSelect}
                                        disabled={processing}
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </DealModalSection>

            <AttachmentPreview
                attachments={dealAttachments}
                initialIndex={previewIndex}
                isOpen={isPreviewOpen}
                onClose={handleClosePreview}
            />
        </>
    );
}
